// AI Generated Component
import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { BaseChart } from '@client/app/modules/charts/models/chart';
import { PageService } from '@client/app/services/entities/page/page.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { safeClone } from '@client/app/shared/utils/object-utils';

@Component({
    selector: 'page-details-section-statistics-traffic-sources',
    standalone: false,
    template: `
        <div class="card panel-monsido-card">
            <div class="card-header py-3">
                <h2 class="font-weight-300 my-0" translate>
                    Traffic sources
                </h2>
            </div>
            <div class="card-body">
                <mon-spinner [loading]="loading">
                    <mon-chart-bar [height]="150"
                               [data]="lineChartData"
                               [labels]="lineChartLabels"
                               [series]="lineChartSeries"
                               [colors]="lineChartColors"
                               [override]="lineChartOverride"
                               [options]="chart.options">
                    </mon-chart-bar>

                    <div class="row mt-5 mb-1">
                        <div class="col-18 offset-2">
                            <div class="position-relative">
                                <span class="vertical-ruler vertical-ruler-left ruler-primary"></span>
                                <notification class="block pl-3" [title]="'Search Engines' | translate" [digit]="counts.organic">
                                </notification>
                            </div>
                        </div>
                        <div class="col-26">
                            <statistics-distribution [visits]="counts.organic" [totalVisits]="counts.total"></statistics-distribution>
                        </div>
                    </div>
                    <div class="row mb-1">
                        <div class="col-18 offset-2">
                            <div class="position-relative">
                                <span class="vertical-ruler vertical-ruler-left ruler-success"></span>
                                <notification class="block pl-3" [title]="'Direct visits' | translate" [digit]="counts.direct">
                                </notification>
                            </div>
                        </div>
                        <div class="col-26">
                            <statistics-distribution [visits]="counts.direct" [totalVisits]="counts.total"></statistics-distribution>
                        </div>
                    </div>
                    <div class="row mb-1">
                        <div class="col-18 offset-2">
                            <div class="position-relative">
                                <span class="vertical-ruler vertical-ruler-left ruler-warning"></span>
                                <notification class="block pl-3" [title]="'Referring visits' | translate" [digit]="counts.referring">
                                </notification>
                            </div>
                        </div>
                        <div class="col-26">
                            <statistics-distribution [visits]="counts.referring" [totalVisits]="counts.total"></statistics-distribution>
                        </div>
                    </div>
                    <div class="row mb-1">
                        <div class="col-18 offset-2">
                            <div class="position-relative">
                                <span class="vertical-ruler vertical-ruler-left ruler-danger"></span>
                                <notification class="block pl-3" [title]="'Campaign visits' | translate" [digit]="counts.paid">
                                </notification>
                            </div>
                        </div>
                        <div class="col-26">
                            <statistics-distribution [visits]="counts.paid" [totalVisits]="counts.total"></statistics-distribution>
                        </div>
                    </div>
                    <div class="row mb-1">
                        <div class="col-18 offset-2">
                            <div class="position-relative">
                                <span class="vertical-ruler vertical-ruler-left ruler-info "></span>
                                <notification class="block pl-3" [title]="'Social media visits' | translate" [digit]="counts.social">
                                </notification>
                            </div>
                        </div>
                        <div class="col-26">
                            <statistics-distribution [visits]="counts.social" [totalVisits]="counts.total"></statistics-distribution>
                        </div>
                    </div>
                </mon-spinner>
            </div>
        </div>
    `,
})
export class PageDetailsSectionStatisticsTrafficSourcesComponent implements OnInit, OnChanges {
    @Input() page: any;
    @Input() startDate: any;
    @Input() endDate: any;

    public remoteData: any = {};
    public counts: any = {
        total: 0,
    };
    public lineChartData: any[] = [];
    public lineChartLabels: any[] = [];
    public lineChartSeries: any[] = [];
    public lineChartColors: any[] = [];
    public lineChartOverride: any[] = [];
    public chart: any;
    public loading: boolean = false;

    constructor (
        private pageService: PageService,
        private dateTimeService: DateTimeService,
        private translateService: TranslateService,
        private pipeService: PipeService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    ngOnChanges (changes: SimpleChanges): void {
        if (changes['startDate'] || changes['endDate']) {
            this.updateDateRange();
        }
    }

    private activate (): void {
        this.remoteData = {};
        this.counts = {
            total: 0,
        };
        this.setupChartSettings();
    }

    private updateDateRange (): void {
        this.setupChart();
    }

    private setupChart (): void {
        const promises: Promise<any>[] = [];
        const params = {
            from: this.startDate.format('YYYY-MM-DD'),
            to: this.endDate.format('YYYY-MM-DD'),
        };
        const types = ['organic', 'direct', 'referring', 'paid', 'social'];
        for (let j = 0; j < types.length; j++) {
            promises.push(this.getVisitorsForType(types[j], params));
        }

        this.lineChartData = [];
        this.loading = true;
        Promise.all(promises)
            .then(() => {
                const typeWithMostData = types.reduce((primaryType, type) => {
                    if (this.remoteData[type].length < this.remoteData[primaryType].length) {
                        return type;
                    }
                    return primaryType;
                }, 'organic');
                this.counts.total = 0;

                types.forEach((type) => {
                    this.lineChartData.push(
                        this.remoteData[type].map((typeData: any) => {
                            return +typeData.visits || 0;
                        }),
                    );
                    this.counts[type] = this.lineChartData[this.lineChartData.length - 1].reduce((num: any, currentValue: any) => {
                        return num + currentValue;
                    }, 0);
                    this.counts.total += this.counts[type];
                });

                this.lineChartLabels = this.remoteData[typeWithMostData].map((typeData: any) => {
                    return this.dateTimeService.format(typeData.date, 'll');
                });
            })
            .catch(() => {
                // Handle error silently like the original
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private setupChartSettings (): void {
        this.lineChartLabels = [];
        this.lineChartOverride = [];
        this.lineChartSeries = [
            this.translateService.getString('Search engine'),
            this.translateService.getString('Direct visits'),
            this.translateService.getString('Referring visits'),
            this.translateService.getString('Campaign visits'),
            this.translateService.getString('Social Media visits'),
        ];
        this.lineChartColors = ['#71a3f6', '#57B754', '#EAA646', '#D24747', '#205C9E'];
        this.chart = new BaseChart();
        this.chart.setTooltipCustom({
            mode: 'label',
            intersect: false,
            callbacks: {
                label: (tooltipItem: any, data: any) => (
                    data.datasets[tooltipItem.datasetIndex].label +
                        ': ' +
                    this.pipeService.get('monNumber')(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index], '0,0')
                ),
            },
        });
        this.chart.setScalesSettings({
            yAxes: [
                {
                    id: 'y-axis-0',
                    type: 'linear',
                    position: 'right',
                    ticks: {
                        beginAtZero: true,
                        suggestedMax: 4,
                        maxTicksLimit: 5,
                        callback: (value: any) => {
                            return this.pipeService.get('monNumber')(value, '0,0');
                        },
                        min: 0,
                    },
                    stacked: true,
                },
            ],
            xAxes: [
                {
                    gridLines: {
                        display: false,
                    },
                    stacked: true,
                    ticks: {
                        callback: (value: any) => {
                            if (value) {
                                return this.dateTimeService.format(value, 'MMM DD', 'll');
                            }
                            return '';
                        },
                    },
                },
            ],
        });

        for (let i = 0; i < this.lineChartSeries.length; i++) {
            this.lineChartOverride.push({
                type: 'bar',
                fill: false,
                borderColor: this.lineChartColors[i],
                borderWidth: 1,
            });
        }
    }

    private getVisitorsForType (type: string, params: any): Promise<any> {
        return this.pageService.getStatVisitors(this.page.id, safeClone(Object.assign(params, { type: type }))).then(
            (data: any) => {
                this.remoteData[type] = data;
            },
            () => {
                this.remoteData[type] = [];
            },
        );
    }
}
